<template>
  <container :isModal="false" :loading="stateLoading">
    <template v-slot:header>
      <h4>Clientes representados</h4>
    </template>
    <template v-slot:body>
      <template v-if="stateReady">
        <fieldset :disabled="stateBlocked">
          <div class="p-formgrid p-grid">
            <div class="p-field p-fluid p-col-12">
              <entity-selector-cliente
                :key="selectorKey"
                label="Adicionar cliente"
                :canEdit="false"
                :parentLoading="stateBlocked"
                @confirm-selection="adicionarRepresentado"
              />
            </div>
            <div class="p-col-12">
              <DataTable
                class="tabela-representados"
                :value="representados"
                dataKey="idCliente"
                responsiveLayout="stack"
                :stripedRows="true"
                :showGridlines="true"
              >
                <template #empty>Nenhum cliente representado.</template>
                <Column field="idCliente" header="Código" />
                <Column field="nome" header="Cliente" />
                <Column header="CPF/CNPJ">
                  <template #body="slotProps">
                    <span v-text="formatCpfCnpj(slotProps.data.cpfCnpj)" />
                  </template>
                </Column>
                <Column header="Bloqueado">
                  <template #body="slotProps">
                    <span v-text="slotProps.data.bloqueado ? 'Sim' : 'Não'" />
                  </template>
                </Column>
                <Column header="Representado desde">
                  <template #body="slotProps">
                    <span
                      v-text="
                        slotProps.data.dataCadastro
                          ? formatDate(slotProps.data.dataCadastro)
                          : 'Ao salvar'
                      "
                    />
                  </template>
                </Column>
                <Column>
                  <template #body="slotProps">
                    <Button
                      icon="fas fa-trash-alt"
                      class="p-button-rounded p-button-danger"
                      v-title="'Remover cliente'"
                      @click="removerRepresentado(slotProps.data)"
                    />
                  </template>
                </Column>
              </DataTable>
            </div>
          </div>
        </fieldset>
      </template>
    </template>
  </container>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  toRefs,
  onMounted,
  computed,
  Ref
} from "vue";
import { useAuth } from "../../../modules/auth";
import { useToast } from "../../../modules/message";
import { format, formatDate } from "../../../modules/utils";
import { ClientePayload } from "../../../modules/business/cliente/clienteBusiness";
import {
  ClienteRepresentadoPayload,
  useClienteRepresentadoService
} from "../../../modules/business/cliente/clienteRepresentadoBusiness";

// eslint-disable-next-line
declare var require: any;

interface ClienteRepresentadosState {
  representados: Array<ClienteRepresentadoPayload>;
  original: Array<number>;
  selectorKey: number;
  stateReady: boolean;
  loadings: Array<Ref<boolean>>;
}

export default defineComponent({
  props: {
    idCliente: { type: Number, required: true },
    parentLoading: Boolean
  },
  setup(props) {
    const state = reactive<ClienteRepresentadosState>({
      representados: [],
      original: [],
      selectorKey: 0,
      stateReady: false,
      loadings: []
    });

    const stateLoading = computed(() => {
      return state.loadings.length !== 0 || !!props.parentLoading;
    });

    const stateBlocked = computed(() => {
      return !!(
        stateLoading.value || !useAuth().amIAllowed("edit", "root-cliente")
      );
    });

    const setRepresentados = (
      representados: Array<ClienteRepresentadoPayload>
    ) => {
      state.representados = representados;
      state.original = representados.map((r) => r.idCliente);
    };

    const adicionarRepresentado = (cliente?: ClientePayload | null) => {
      // remonta o seletor para ficar vazio e pronto para o próximo
      state.selectorKey++;
      if (!cliente || !cliente.idCliente) {
        return;
      }
      if (cliente.idCliente === props.idCliente) {
        useToast().warning("O parceiro não pode representar a si mesmo");
        return;
      }
      if (state.representados.some((r) => r.idCliente === cliente.idCliente)) {
        useToast().warning("Cliente já está na lista");
        return;
      }
      state.representados.push({
        idCliente: cliente.idCliente,
        nome: cliente.nome,
        cpfCnpj: cliente.cpfCnpj,
        bloqueado: cliente.bloqueado
      });
    };

    const removerRepresentado = (representado: ClienteRepresentadoPayload) => {
      state.representados = state.representados.filter(
        (r) => r.idCliente !== representado.idCliente
      );
    };

    // pessoa física vem com o CPF do responsável (11 dígitos)
    const formatCpfCnpj = (cpfCnpj?: string | null) => {
      if (!cpfCnpj) {
        return "";
      }
      return cpfCnpj.length === 11
        ? format("###.###.###-##", cpfCnpj)
        : format("##.###.###/####-##", cpfCnpj);
    };

    onMounted(() => {
      const {
        data,
        loading,
        getRepresentados
      } = useClienteRepresentadoService();
      state.loadings.push(loading);
      getRepresentados(props.idCliente)
        .then(() => {
          setRepresentados(data.value ?? []);
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          state.stateReady = true;
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    });

    // Chamado pelo CadastroCliente depois de salvar o cliente (botão único)
    const salvarRepresentados = (): Promise<void> => {
      if (!state.stateReady) {
        return Promise.resolve();
      }

      const atuais = state.representados.map((r) => r.idCliente);
      const alterou =
        atuais.length !== state.original.length ||
        atuais.some((id) => !state.original.includes(id));
      if (!alterou) {
        return Promise.resolve();
      }

      const {
        data,
        loading,
        updateRepresentados
      } = useClienteRepresentadoService();
      state.loadings.push(loading);
      return updateRepresentados(props.idCliente, state.representados)
        .then(() => {
          setRepresentados(data.value ?? []);
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        })
        .catch((e) => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          throw e;
        });
    };

    return {
      stateLoading,
      stateBlocked,
      adicionarRepresentado,
      removerRepresentado,
      salvarRepresentados,
      formatCpfCnpj,
      formatDate,
      ...toRefs(state)
    };
  },
  beforeCreate() {
    // import tardio: EntitySelectorCliente -> CrudCliente -> CadastroCliente -> este componente
    if (this.$options.components) {
      this.$options.components.EntitySelectorCliente = require("../EntitySelectorCliente.vue").default;
    }
  },
  components: {}
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
/* listra do tema (rgba 0.02) quase não aparece: branco e cinza bem marcados */
.tabela-representados :deep(.p-datatable-tbody > tr:nth-child(odd)) {
  background: $bg-surface !important;
}

.tabela-representados :deep(.p-datatable-tbody > tr:nth-child(even)) {
  background: $bg-color !important;
}

.tabela-representados :deep(.p-datatable-thead > tr > th),
.tabela-representados :deep(.p-datatable-tbody > tr > td) {
  border: 1px solid $border-color !important;
}
</style>
